import { RefreshCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PDFContent } from "./PDFContent";
import { usePDFExport } from "@/hooks/use-pdf-export";
import { useNavigate } from "react-router-dom";

interface ResultsActionsProps {
  onStartNew: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  savedAssessmentId?: string | null;
  results: {
    organizationDetails: any;
    maturityLevel: string;
    overallScore: number;
    description: string;
    dimensionScores: Record<string, number>;
  };
  isViewMode?: boolean;
}

export const ResultsActions = ({ 
  onStartNew, 
  onSave,
  isSaving,
  savedAssessmentId,
  results, 
  isViewMode = false 
}: ResultsActionsProps) => {
  const { pdfContentRef } = usePDFExport('AIMAA-Assessment-Report.pdf');
  const navigate = useNavigate();

  const handleStartNew = () => {
    // If in view mode (assessment detail), navigate directly to assessment page
    if (isViewMode) {
      navigate('/assessment');
    } else {
      // Otherwise, use the provided onStartNew callback for in-progress assessments
      onStartNew();
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
      <Button 
        onClick={handleStartNew} 
        variant="outline" 
        className="flex items-center gap-2 border-[#0f2d69] text-[#0f2d69] hover:bg-[#0f2d69] hover:text-white"
      >
        <RefreshCcw className="w-4 h-4" />
        Start New Assessment
      </Button>

      {!isViewMode && onSave && (
        <Button 
          onClick={onSave}
          disabled={isSaving || !!savedAssessmentId}
          className="flex items-center gap-2"
          variant={savedAssessmentId ? "outline" : "default"}
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : savedAssessmentId ? 'Saved' : 'Save Assessment'}
        </Button>
      )}

      <PDFContent
        ref={pdfContentRef}
        organizationDetails={results.organizationDetails}
        maturityLevel={results.maturityLevel}
        overallScore={results.overallScore}
        description={results.description}
        dimensionScores={results.dimensionScores}
        assessmentId="pdf-export"
      />
    </div>
  );
};
